import UnifiedBankingSignupFlow from 'components/pages/Banks/BankingSignUp/components/UnifiedBankingSignupFlow';
import { worldThemeContext } from 'hooks/shared/useWorldTheme';
import { useCallback, type ReactNode } from 'react';
import useModal from 'hooks/useModal';

/** Resolved modal label passed to {@link useModal} (accessibility / devtools). */
const DEFAULT_MODAL_LABEL = 'Accountable Banking signup modal';

/**
 * Options for {@link useBankingSignupModal}.
 */
export type UseBankingSignupModalOptions = {
  /**
   * Short label for this entry point (e.g. `"Bank settings"`).
   * Shown as `Accountable Banking signup - {name}` on the underlying modal.
   * Omit for a generic label when the source does not need to be distinguished.
   */
  name?: string;

  /**
   * Called after the user finishes the flow and the modal has closed
   * (after {@link UnifiedBankingSignupFlow} invokes its `onComplete`).
   */
  onComplete?: () => void;
};

/**
 * Return value of {@link useBankingSignupModal}.
 */
export type UseBankingSignupModalResult = {
  /** Render next to your page content so the modal portal mounts (required for `openSignupFlow` to work). */
  modalContent: ReactNode;

  /**
   * Opens the signup / activation modal.
   * @param initialStepId - When set, the flow tries to resume at this step (e.g. after a redirect).
   */
  openSignupFlow: (initialStepId?: string) => void;
};

/**
 * Opens the unified Accountable Banking signup and post-account activation flow inside a bank-themed modal.
 *
 * Typical usage:
 *
 * ```tsx
 * const { modalContent, openSignupFlow } = useBankingSignupModal({
 *   name: 'Bank settings',
 *   onComplete: () => refetch(),
 * });
 *
 * return (
 *   <>
 *     {modalContent}
 *     <button onClick={() => openSignupFlow()}>Open</button>
 *   </>
 * );
 * ```
 *
 * @param options - Optional `name` (modal identification) and `onComplete` (post-close side effects).
 */
export function useBankingSignupModal(
  options: UseBankingSignupModalOptions = {},
): UseBankingSignupModalResult {
  const { name, onComplete } = options;

  const modalName =
    name && name.length > 0
      ? `Accountable Banking signup - ${name}`
      : DEFAULT_MODAL_LABEL;

  const [modalContent, modalApi] = useModal({
    width: '2xl',
    name: modalName,
    color: 'bank',
    className: '!max-w-[44rem]',
    overlayClassName: 'backdrop-blur-sm',
  });

  const openSignupFlow = useCallback(
    (initialStepId?: string) => {
      modalApi.open(() => (
        <worldThemeContext.Provider value="bank">
          <UnifiedBankingSignupFlow
            initialStepId={initialStepId}
            onComplete={() => {
              modalApi.close();
              onComplete?.();
            }}
          />
        </worldThemeContext.Provider>
      ));
    },
    [modalApi, onComplete],
  );

  return { modalContent, openSignupFlow };
}
